import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  DialogContentText,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { listExports, updateExport, deleteExport, getExport } from '../services/api';
import { ExportResponse } from '../types';
import AssignCoralDialog from '../components/AssignCoralDialog';
import { accent } from '../theme';

// Converts a YYYY-MM-DD date input value back to a full ISO-8601 datetime
// for the backend, which expects exportDate as ISO-8601 (not a bare date).
const formatDateForBackend = (dateString: string): string => {
  return new Date(dateString).toISOString().replace(/\.\d{3}Z$/, 'Z');
};

const Exports: React.FC = () => {
  // Data state
  const [exports, setExports] = useState<ExportResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  // Filter state - separate variables for UI
  const [company, setCompany] = useState('');
  const [exportName, setExportName] = useState('');
  const [exportDate, setExportDate] = useState('');

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [exportToEdit, setExportToEdit] = useState<ExportResponse | null>(null);
  const [updating, setUpdating] = useState(false);
  const [editFormData, setEditFormData] = useState({
    company: '',
    exportName: '',
    exportDate: '',
    cites: '',
  });

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportToDelete, setExportToDelete] = useState<ExportResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Assign coral dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedExportForAssignment, setSelectedExportForAssignment] =
    useState<ExportResponse | null>(null);

  // Track if component has mounted
  const hasInitialized = useRef(false);

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // Helper function to fetch exports with given filter values
  const fetchExportsWithFilters = useCallback(
    async (
      cursor?: string,
      companyFilter?: string,
      exportNameFilter?: string,
      exportDateFilter?: string
    ) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listExports(
          20,
          cursor,
          companyFilter || undefined,
          exportNameFilter || undefined,
          exportDateFilter || undefined
        );
        setExports(result.items);
        setNextCursor(result.nextCursor || null);
      } catch (err: any) {
        setError(err.message || 'Failed to load exports');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleRefresh = () => {
    setCompany('');
    setExportName('');
    setExportDate('');
    setCursorHistory([]);
    setNextCursor(null);
    fetchExportsWithFilters(undefined, '', '', '');
  };

  const handleSearch = async () => {
    // Reset pagination when searching
    setCursorHistory([]);
    setNextCursor(null);
    await fetchExportsWithFilters(undefined, company, exportName, exportDate);
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (nextCursor) {
      setCursorHistory((prev) => [...prev, nextCursor]);
      fetchExportsWithFilters(nextCursor, company, exportName, exportDate);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const prevCursor = cursorHistory[cursorHistory.length - 2];
      setCursorHistory((prev) => prev.slice(0, -1));
      fetchExportsWithFilters(prevCursor, company, exportName, exportDate);
    } else {
      // Go to first page with current filters
      setCursorHistory([]);
      setNextCursor(null);
      fetchExportsWithFilters(undefined, company, exportName, exportDate);
    }
  };

  // Edit dialog handlers
  const handleEditClick = async (exportItem: ExportResponse) => {
    try {
      // Fetch the fresh export data with coral stats
      const freshExport = await getExport(exportItem.exportId);
      setExportToEdit(freshExport);
      setEditFormData({
        company: freshExport.company,
        exportName: freshExport.exportName || '',
        exportDate: freshExport.exportDate.split('T')[0], // Extract just the date part (YYYY-MM-DD)
        cites: freshExport.cites || '',
      });
      setEditDialogOpen(true);
    } catch (err: any) {
      console.error('Failed to fetch export details:', err);
      setError(err.message || 'Failed to load export details');
    }
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setExportToEdit(null);
    setEditFormData({
      company: '',
      exportName: '',
      exportDate: '',
      cites: '',
    });
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditSave = async () => {
    if (!exportToEdit) return;

    setUpdating(true);
    try {
      await updateExport(exportToEdit.exportId, {
        ...editFormData,
        exportDate: formatDateForBackend(editFormData.exportDate),
      });
      handleEditClose();
      // Refresh the list with current filters and reset to first page
      setCursorHistory([]);
      setNextCursor(null);
      await fetchExportsWithFilters(undefined, company, exportName, exportDate);
    } catch (error: any) {
      console.error('Failed to update export:', error);
      setError(error.message || 'Failed to update export');
    } finally {
      setUpdating(false);
    }
  };

  // Delete dialog handlers
  const handleDeleteClick = (exportItem: ExportResponse) => {
    setExportToDelete(exportItem);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setExportToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!exportToDelete) return;

    setDeleting(true);
    try {
      await deleteExport(exportToDelete.exportId);
      handleDeleteClose();
      // Refresh the list with current filters and reset to first page
      setCursorHistory([]);
      setNextCursor(null);
      await fetchExportsWithFilters(undefined, company, exportName, exportDate);
    } catch (error: any) {
      console.error('Failed to delete export:', error);
      setError(error.message || 'Failed to delete export');
    } finally {
      setDeleting(false);
    }
  };

  // Assign coral dialog handlers
  const handleAssignClick = (exportItem: ExportResponse) => {
    setSelectedExportForAssignment(exportItem);
    setAssignDialogOpen(true);
  };

  const handleAssignClose = () => {
    setAssignDialogOpen(false);
    setSelectedExportForAssignment(null);
  };

  const handleAssignSuccess = () => {
    // Refresh the list after successful assignment
    fetchExportsWithFilters(undefined, company, exportName, exportDate);
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      console.log('useEffect triggered - calling API for initial load');
      fetchExportsWithFilters(undefined, '', '', '');
      hasInitialized.current = true;
    }
  }, [fetchExportsWithFilters]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Filter Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 1, ...accent.coral }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Filter Export Records
        </Typography>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
            }}
          >
            <TextField
              label="Company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Search by company name..."
              size="small"
              variant="outlined"
            />
            <TextField
              label="Export Name"
              value={exportName}
              onChange={(e) => setExportName(e.target.value)}
              placeholder="Search by export name..."
              size="small"
              variant="outlined"
            />
            <TextField
              label="Export Date (from)"
              value={exportDate}
              onChange={(e) => setExportDate(e.target.value)}
              type="date"
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleSearch} disabled={loading}>
              Search
            </Button>
            {(company || exportName || exportDate) && (
              <Button variant="outlined" size="small" onClick={handleRefresh} disabled={loading}>
                Clear
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Results Section */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, boxShadow: 1, ...accent.navy }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Export Records ({exports.length})</Typography>
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Export Name</TableCell>
                <TableCell>Export Date</TableCell>
                <TableCell>Cites</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    Loading exports...
                  </TableCell>
                </TableRow>
              ) : exports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No results found. Try adjusting your filters or refresh to view all exports.
                  </TableCell>
                </TableRow>
              ) : (
                exports.map((exportItem) => (
                  <TableRow key={exportItem.exportId} hover>
                    <TableCell sx={{ fontWeight: 'medium' }}>{exportItem.company}</TableCell>
                    <TableCell>
                      {exportItem.exportName ? (
                        <Chip
                          label={exportItem.exportName}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDateTime(exportItem.exportDate)}</TableCell>
                    <TableCell>{exportItem.cites}</TableCell>
                    <TableCell>{formatDateTime(exportItem.createdAt)}</TableCell>
                    <TableCell>{formatDateTime(exportItem.updatedAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Manage Corals">
                        <IconButton
                          onClick={() => handleAssignClick(exportItem)}
                          color="info"
                          size="small"
                        >
                          <AssignmentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Export">
                        <IconButton
                          onClick={() => handleEditClick(exportItem)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Export">
                        <IconButton
                          onClick={() => handleDeleteClick(exportItem)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {exports.length > 0 && (
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}
          >
            <Button
              variant="outlined"
              onClick={handlePrevPage}
              disabled={loading || cursorHistory.length === 0}
              size="small"
            >
              Previous Page
            </Button>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                const currentPage = cursorHistory.length + 1;
                const startRecord = (currentPage - 1) * 20 + 1;
                const endRecord = startRecord + exports.length - 1;
                return `Showing ${startRecord}-${endRecord} (Page ${currentPage})`;
              })()}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleNextPage}
              disabled={loading || !nextCursor}
              size="small"
            >
              Next Page
            </Button>
          </Box>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Export</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 2 }}>
            <TextField
              label="Company"
              value={editFormData.company}
              onChange={(e) => handleEditFormChange('company', e.target.value)}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Export Name"
              value={editFormData.exportName}
              onChange={(e) => handleEditFormChange('exportName', e.target.value)}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Export Date"
              value={editFormData.exportDate}
              onChange={(e) => handleEditFormChange('exportDate', e.target.value)}
              fullWidth
              variant="outlined"
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Cites"
              value={editFormData.cites}
              onChange={(e) => handleEditFormChange('cites', e.target.value)}
              fullWidth
              variant="outlined"
              multiline
              rows={2}
            />

            {/* Coral Stats Section */}
            {exportToEdit && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Coral Assignment Stats
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Corals Assigned:{' '}
                      <span style={{ fontWeight: 'bold' }}>
                        {exportToEdit.assignedCoral?.length || 0}
                      </span>
                    </Typography>
                  </Box>

                  {exportToEdit.coralCounts &&
                    Object.keys(exportToEdit.coralCounts).filter((key) => key !== 'totalCount')
                      .length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          <strong>Species Breakdown:</strong>
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          {Object.entries(exportToEdit.coralCounts)
                            .filter(([species]) => species !== 'totalCount')
                            .map(([species, count]) => (
                              <Typography key={species} variant="body2" color="text.secondary">
                                • {species}: {count}
                              </Typography>
                            ))}
                        </Box>
                      </Box>
                    )}

                  {(!exportToEdit.coralCounts ||
                    Object.keys(exportToEdit.coralCounts).filter((key) => key !== 'totalCount')
                      .length === 0) &&
                    (!exportToEdit.assignedCoral || exportToEdit.assignedCoral.length === 0) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        No corals assigned to this export
                      </Typography>
                    )}
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={updating}>
            Cancel
          </Button>
          <Button onClick={handleEditSave} variant="contained" disabled={updating}>
            {updating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Export</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this export record?
          </DialogContentText>
          <Box sx={{ my: 2 }}>
            <Typography variant="body2">
              <Typography component="span" fontWeight="bold">
                Company:
              </Typography>{' '}
              {exportToDelete?.company}
            </Typography>
            {exportToDelete?.exportName && (
              <Typography variant="body2">
                <Typography component="span" fontWeight="bold">
                  Export Name:
                </Typography>{' '}
                {exportToDelete.exportName}
              </Typography>
            )}
            <Typography variant="body2">
              <Typography component="span" fontWeight="bold">
                Export Date:
              </Typography>{' '}
              {exportToDelete && formatDateTime(exportToDelete.exportDate)}
            </Typography>
          </Box>
          <DialogContentText>This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={<DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Coral Dialog */}
      <AssignCoralDialog
        open={assignDialogOpen}
        exportId={selectedExportForAssignment?.exportId || ''}
        export={selectedExportForAssignment || undefined}
        onClose={handleAssignClose}
        onSuccess={handleAssignSuccess}
      />
    </Container>
  );
};

export default Exports;
